/**
 * API Service Configuration
 * Centralizes all API calls with proper authentication
 */

import authService from './authService'

const API_BASE_URL = process.env.VUE_APP_API_URL || 'http://localhost:3001'

/**
 * Make authenticated API request
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Fetch options
 * @returns {Promise} Response promise
 */
export async function apiRequest(endpoint, options = {}) {
  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`
  
  // Get auth headers from authService
  let authHeaders = authService.getAuthHeader()
  
  // Fallback: try to get token directly from storage if authService doesn't have it
  if (!authHeaders.Authorization) {
    const token = localStorage.getItem('budget_auth_token') || sessionStorage.getItem('budget_auth_token')
    if (token) {
      authHeaders = { 'Authorization': `Bearer ${token}` }
    }
  }
  
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...options.headers
    }
  }
  
  const finalOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers
    }
  }
  
  try {
    const response = await fetch(url, finalOptions)
    
    // Handle 401 - token expired or invalid
    if (response.status === 401) {
      // Try to refresh token
      const refreshed = await authService.refreshAuthToken()
      if (refreshed) {
        // Retry request with new token
        const newAuthHeaders = authService.getAuthHeader()
        finalOptions.headers = {
          ...finalOptions.headers,
          ...newAuthHeaders
        }
        return fetch(url, finalOptions)
      } else {
        // Refresh failed, redirect to login
        authService.clearAuthData()
        window.location.href = '/login'
        throw new Error('Authentication failed')
      }
    }
    
    return response
  } catch (error) {
    console.error('API request error:', error)
    throw error
  }
}

/**
 * Admin API endpoints
 */
export const adminAPI = {
  // Users
  async getUsers(params = {}) {
    const queryString = new URLSearchParams(params).toString()
    const response = await apiRequest(`/api/admin/users${queryString ? '?' + queryString : ''}`)
    return response.json()
  },
  
  async createUser(userData) {
    const response = await apiRequest('/api/admin/users', {
      method: 'POST',
      body: JSON.stringify(userData)
    })
    return response.json()
  },
  
  async updateUser(userId, userData) {
    const response = await apiRequest(`/api/admin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData)
    })
    return response.json()
  },
  
  async deleteUser(userId) {
    const response = await apiRequest(`/api/admin/users/${userId}`, {
      method: 'DELETE'
    })
    return response.json()
  },
  
  // Dashboard
  async getDashboard() {
    const response = await apiRequest('/api/admin/dashboard')
    return response.json()
  },

  async getDashboardStats() {
    const response = await apiRequest('/api/admin/dashboard/stats')
    return response.json()
  },

  async getDashboardCharts() {
    const response = await apiRequest('/api/admin/dashboard/charts')
    return response.json()
  },

  async getDashboardTopData() {
    const response = await apiRequest('/api/admin/dashboard/top-data')
    return response.json()
  },

  async getDashboardActivity() {
    const response = await apiRequest('/api/admin/dashboard/activity')
    return response.json()
  },
  
  // Equipos
  async getEquipos(params = {}) {
    const queryString = new URLSearchParams(params).toString()
    const response = await apiRequest(`/api/admin/equipos${queryString ? '?' + queryString : ''}`)
    return response.json()
  },
  
  async getEquipo(equipoId) {
    const response = await apiRequest(`/api/admin/equipos/${equipoId}`)
    return response.json()
  },
  
  async createEquipo(equipoData) {
    const response = await apiRequest('/api/admin/equipos', {
      method: 'POST',
      body: JSON.stringify(equipoData)
    })
    return response.json()
  },
  
  async updateEquipo(equipoId, equipoData) {
    const response = await apiRequest(`/api/admin/equipos/${equipoId}`, {
      method: 'PUT',
      body: JSON.stringify(equipoData)
    })
    return response.json()
  },
  
  async deleteEquipo(equipoId) {
    const response = await apiRequest(`/api/admin/equipos/${equipoId}`, {
      method: 'DELETE'
    })
    return response.json()
  },
  
  // Roles
  async getRoles(params = {}) {
    const queryString = new URLSearchParams(params).toString()
    const response = await apiRequest(`/api/admin/roles${queryString ? '?' + queryString : ''}`)
    return response.json()
  },
  
  async getRole(roleId) {
    const response = await apiRequest(`/api/admin/roles/${roleId}`)
    return response.json()
  },
  
  async createRole(roleData) {
    const response = await apiRequest('/api/admin/roles', {
      method: 'POST',
      body: JSON.stringify(roleData)
    })
    return response.json()
  },
  
  async updateRole(roleId, roleData) {
    const response = await apiRequest(`/api/admin/roles/${roleId}`, {
      method: 'PUT',
      body: JSON.stringify(roleData)
    })
    return response.json()
  },
  
  async deleteRole(roleId) {
    const response = await apiRequest(`/api/admin/roles/${roleId}`, {
      method: 'DELETE'
    })
    return response.json()
  },
  
  // Peoples
  async getPeoples(params = {}) {
    const queryString = new URLSearchParams(params).toString()
    const response = await apiRequest(`/api/admin/peoples${queryString ? '?' + queryString : ''}`)
    return response.json()
  },
  
  async getPeople(peopleId) {
    const response = await apiRequest(`/api/admin/peoples/${peopleId}`)
    return response.json()
  },
  
  async createPeople(peopleData) {
    const response = await apiRequest('/api/admin/peoples', {
      method: 'POST',
      body: JSON.stringify(peopleData)
    })
    return response.json()
  },
  
  async updatePeople(peopleId, peopleData) {
    const response = await apiRequest(`/api/admin/peoples/${peopleId}`, {
      method: 'PUT',
      body: JSON.stringify(peopleData)
    })
    return response.json()
  },
  
  async deletePeople(peopleId) {
    const response = await apiRequest(`/api/admin/peoples/${peopleId}`, {
      method: 'DELETE'
    })
    return response.json()
  },
  
  // Teams
  async getTeams(params = {}) {
    const queryString = new URLSearchParams(params).toString()
    const response = await apiRequest(`/api/admin/teams${queryString ? '?' + queryString : ''}`)
    return response.json()
  },
  
  async createTeam(teamData) {
    const response = await apiRequest('/api/admin/teams', {
      method: 'POST',
      body: JSON.stringify(teamData)
    })
    return response.json()
  },
  
  async updateTeam(teamId, teamData) {
    const response = await apiRequest(`/api/admin/teams/${teamId}`, {
      method: 'PUT',
      body: JSON.stringify(teamData)
    })
    return response.json()
  },
  
  async deleteTeam(teamId) {
    const response = await apiRequest(`/api/admin/teams/${teamId}`, {
      method: 'DELETE'
    })
    return response.json()
  },
  
  // Groups
  async getGroups(params = {}) {
    const queryString = new URLSearchParams(params).toString()
    const response = await apiRequest(`/api/admin/groups${queryString ? '?' + queryString : ''}`)
    return response.json()
  },
  
  async createGroup(groupData) {
    const response = await apiRequest('/api/admin/groups', {
      method: 'POST',
      body: JSON.stringify(groupData)
    })
    return response.json()
  },
  
  async updateGroup(groupId, groupData) {
    const response = await apiRequest(`/api/admin/groups/${groupId}`, {
      method: 'PUT',
      body: JSON.stringify(groupData)
    })
    return response.json()
  },
  
  async deleteGroup(groupId) {
    const response = await apiRequest(`/api/admin/groups/${groupId}`, {
      method: 'DELETE'
    })
    return response.json()
  },
  
  // Items
  async getItems(params = {}) {
    const queryString = new URLSearchParams(params).toString()
    const response = await apiRequest(`/api/admin/items${queryString ? '?' + queryString : ''}`)
    return response.json()
  },
  
  async createItem(itemData) {
    const response = await apiRequest('/api/admin/items', {
      method: 'POST',
      body: JSON.stringify(itemData)
    })
    return response.json()
  },
  
  async updateItem(itemId, itemData) {
    const response = await apiRequest(`/api/admin/items/${itemId}`, {
      method: 'PUT',
      body: JSON.stringify(itemData)
    })
    return response.json()
  },
  
  async deleteItem(itemId) {
    const response = await apiRequest(`/api/admin/items/${itemId}`, {
      method: 'DELETE'
    })
    return response.json()
  },
  
  // Budgets
  async getBudgets(params = {}) {
    const queryString = new URLSearchParams(params).toString()
    const response = await apiRequest(`/api/admin/budgets${queryString ? '?' + queryString : ''}`)
    return response.json()
  },
  
  async deleteBudget(budgetId) {
    const response = await apiRequest(`/api/admin/budgets/${budgetId}`, {
      method: 'DELETE'
    })
    return response.json()
  }
}

export default {
  apiRequest,
  adminAPI
}